import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { useMotionValue } from 'framer-motion'
import { todoEntryKey, type TodoEntry } from './todos-order'
import { placeTodoInPinned, type PinnedTodoDropTarget } from './pinned-todo-drop'
import { usePostDragClickGuard } from '../use-post-drag-click-guard'
import { useDragAutoScroll } from '../use-drag-auto-scroll'

interface DragPreview {
  title: string
  width: number
  height: number
}

export function usePinnedTodoDrag(order: Array<string>, move: (todo: TodoEntry, target: PinnedTodoDropTarget) => void) {
  const listRef = useRef<HTMLDivElement>(null)
  const current = useRef({ order, move })
  current.current = { order, move }
  const cleanupRef = useRef<(() => void) | null>(null)
  const [preview, setPreview] = useState<DragPreview | null>(null)
  const previewX = useMotionValue(0)
  const previewY = useMotionValue(0)
  const { begin: beginSuppressingClick, end: endSuppressingClick, shouldSuppress } = usePostDragClickGuard()
  const suppressClick = (event: ReactMouseEvent) => {
    if (shouldSuppress()) {
      event.preventDefault()
      event.stopPropagation()
    }
  }
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [target, setTarget] = useState<PinnedTodoDropTarget | null>(null)
  const { start: startAutoScroll, stop: stopAutoScroll } = useDragAutoScroll()
  useEffect(() => () => cleanupRef.current?.(), [])

  const startDrag = (event: ReactPointerEvent<HTMLElement>, todo: TodoEntry) => {
    const id = todoEntryKey(todo)
    const list = listRef.current
    if (event.button !== 0 || !list || cleanupRef.current) {
      return
    }
    event.stopPropagation()
    const row = event.currentTarget
    const bounds = row.getBoundingClientRect()
    const title = row.querySelector('[data-todo-view-target]')?.textContent ?? row.dataset.todoDragTitle ?? 'To-do'
    const pointerId = event.pointerId
    const startX = event.clientX
    const startY = event.clientY
    let x = startX
    let y = startY
    let active = false
    const previousUserSelect = document.body.style.userSelect
    let destination: PinnedTodoDropTarget | null = null

    const updateTarget = () => {
      const hit = document.elementFromPoint(x, y)
      const zone = hit?.closest<HTMLElement>('[data-pinned-drop-zone]')
      const todosZone = hit?.closest<HTMLElement>('[data-todos-drop-zone]')
      let row = hit?.closest<HTMLElement>('[data-pinned-todo]')
      if (!row && !todosZone) {
        // Include the gap where the insertion marker is drawn.
        row = [...list.querySelectorAll<HTMLElement>('[data-pinned-todo]')].find((candidate) => {
          const bounds = candidate.getBoundingClientRect()
          return x >= bounds.left - 8 && x <= bounds.right && y >= bounds.top - 8 && y <= bounds.bottom + 8
        })
      }
      const targetId = row?.dataset.pinnedTodo
      let next: PinnedTodoDropTarget | null = null
      if (todosZone && list.contains(todosZone) && current.current.order.includes(id)) {
        next = { section: 'todos', id: null, placement: 'before' }
      } else if (row && list.contains(row) && targetId && current.current.order.includes(targetId)) {
        const bounds = row.getBoundingClientRect()
        const midpoint = bounds.top + bounds.height / 2
        // Small pointer movements around the midpoint must not flicker the insertion line.
        const placement =
          destination?.id === targetId && Math.abs(y - midpoint) < 4
            ? destination.placement
            : y < midpoint
              ? 'before'
              : 'after'
        const reordered = placeTodoInPinned(current.current.order, id, { id: targetId, placement })
        if (reordered.some((key, index) => key !== current.current.order[index])) {
          next = { id: targetId, placement }
        }
      } else if (zone && list.contains(zone)) {
        const reordered = placeTodoInPinned(current.current.order, id, { id: null, placement: 'before' })
        if (
          reordered.length !== current.current.order.length ||
          reordered.some((key, index) => key !== current.current.order[index])
        ) {
          next = { id: null, placement: 'before' }
        }
      }
      if (
        destination?.id !== next?.id ||
        destination?.placement !== next?.placement ||
        destination?.section !== next?.section
      ) {
        destination = next
        setTarget(next)
      }
    }

    const onPointerMove = (pointer: PointerEvent) => {
      if (pointer.pointerId !== pointerId) {
        return
      }
      x = pointer.clientX
      y = pointer.clientY
      if (!active) {
        if (Math.hypot(x - startX, y - startY) < 4) {
          return
        }
        active = true
        beginSuppressingClick()
        document.body.style.userSelect = 'none'
        // Capture only after moving, so an ordinary click still opens the row.
        list.setPointerCapture(pointerId)
        window.getSelection()?.removeAllRanges()
        setPreview({ title, width: bounds.width / 2, height: bounds.height })
        setDraggedId(id)
        startAutoScroll(list, y)
      }
      pointer.preventDefault()
      previewX.set(x - (startX - bounds.left) / 2)
      previewY.set(bounds.top + y - startY)
      updateTarget()
    }

    const cleanup = () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerCancel)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('blur', cancel)
      list.removeEventListener('lostpointercapture', cancel)
      window.removeEventListener('scroll', updateTarget, true)
      stopAutoScroll()
      if (active) {
        document.body.style.userSelect = previousUserSelect
      }
      cleanupRef.current = null
      if (list.hasPointerCapture(pointerId)) {
        list.releasePointerCapture(pointerId)
      }
    }
    const cancel = () => {
      cleanup()
      setDraggedId(null)
      setTarget(null)
      setPreview(null)
      if (active) {
        endSuppressingClick()
      }
    }
    const onPointerUp = (pointer: PointerEvent) => {
      if (pointer.pointerId !== pointerId) {
        return
      }
      x = pointer.clientX
      y = pointer.clientY
      updateTarget()
      const drop = destination
      cancel()
      if (active && drop) {
        current.current.move(todo, drop)
      }
    }
    const onPointerCancel = (pointer: PointerEvent) => {
      if (pointer.pointerId === pointerId) {
        cancel()
      }
    }
    const onKeyDown = (key: KeyboardEvent) => {
      if (key.key === 'Escape') {
        key.preventDefault()
        cancel()
      }
    }

    cleanupRef.current = cleanup
    window.addEventListener('pointermove', onPointerMove, { passive: false })
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerCancel)
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('blur', cancel)
    list.addEventListener('lostpointercapture', cancel)
    window.addEventListener('scroll', updateTarget, true)
  }

  return { listRef, draggedId, target, startDrag, preview, previewX, previewY, suppressClick }
}
